/**
 * A To / Cc / Bcc line (core logic/mail `recipientChips`, `typedRecipients`, `rankContacts`): chips, then the text being
 * typed, as the chat's New message picker does it. A separator, Enter, Tab, leaving the field or pasting a list makes
 * chips; pressing one puts it back to edit; Backspace on the empty field removes the last. `value` stays the plain
 * text the composer saves, with the text still being typed at the end, so drafts and Send read it as before.
 */
import * as Mail from "@bundle/core/logic/mail";
import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n/I18nProvider.tsx";
import { Avatar, Popover, Pressable, SearchChipField, Stack, Text, type SearchChipFieldKey, type SearchChipItem } from "../primitives/index.ts";

export type RecipientContact = Mail.RankableContact & { id: string };

export type RecipientFieldProps = {
  value: string;
  onChange: (value: string) => void;
  /** Who type-ahead offers. */
  contacts: readonly RecipientContact[];
  now: number;
  avatarUrl?: (email: string) => string | undefined;
  accessibilityLabel: string;
  placeholder?: string;
  autoFocus?: boolean;
  /** Puts the keyboard here whenever it changes. */
  focusSeq?: number;
  testID: string;
};

export function RecipientField({ value, onChange, contacts, now, avatarUrl, accessibilityLabel, placeholder, autoFocus, focusSeq, testID }: RecipientFieldProps) {
  const t = useT();
  const [chips, setChips] = useState(() => Mail.recipientChips(value).map((c) => c.text));
  const [draft, setDraft] = useState("");
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [refocus, setRefocus] = useState(0);
  const said = useRef(value);
  // Changed from outside (a template, Add contact, an undone send): that's the list now.
  useEffect(() => {
    if (value === said.current) return;
    said.current = value;
    setChips(Mail.recipientChips(value).map((c) => c.text));
    setDraft("");
  }, [value]);

  const shown = Mail.recipientChips(chips.join(", "));
  const set = (nextChips: string[], nextDraft: string) => {
    setChips(nextChips);
    setDraft(nextDraft);
    setActive(0);
    setDismissed(false);
    said.current = Mail.joinRecipients(nextChips, nextDraft);
    onChange(said.current);
  };
  const texts = shown.map((c) => c.text);
  const type = (text: string) => {
    const { done, rest } = Mail.typedRecipients(text);
    set([...texts, ...done], rest);
  };
  const finish = () => {
    if (draft.trim() !== "") set([...texts, draft.trim()], "");
  };
  const remove = (i: number) => set(texts.filter((_, x) => x !== i), draft);
  const edit = (i: number) => {
    const others = texts.filter((_, x) => x !== i);
    set(draft.trim() === "" ? others : [...others, draft.trim()], texts[i]!);
    setRefocus((n) => n + 1);
  };
  const matches = dismissed ? [] : Mail.rankContacts(contacts, draft, now, shown.flatMap((c) => (c.address === undefined ? [] : [c.address])));
  const open = matches.length > 0;
  const pick = (c: RecipientContact) => {
    set([...texts, Mail.formatAddress(c.name === undefined ? { address: c.email } : { address: c.email, name: c.name })], "");
    setRefocus((n) => n + 1);
  };
  const url = (email: string) => avatarUrl?.(email);
  const key = (k: SearchChipFieldKey): boolean => {
    if (k === "backspace") {
      if (draft !== "" || texts.length === 0) return false;
      remove(texts.length - 1);
      return true;
    }
    if (open && (k === "up" || k === "down")) {
      setActive((active + (k === "up" ? matches.length - 1 : 1)) % matches.length);
      return true;
    }
    if (open && (k === "enter" || k === "tab")) {
      pick(matches[Math.min(active, matches.length - 1)]!);
      return true;
    }
    if (open && k === "escape") {
      setDismissed(true);
      return true;
    }
    if (k === "enter" && draft.trim() !== "") {
      finish();
      return true;
    }
    if (k === "tab") finish();
    return false;
  };
  const items = shown.map((c, i): SearchChipItem => {
    const item: SearchChipItem = {
      id: String(i),
      label: c.label,
      onPress: () => edit(i),
      accessibilityLabel: t("mail.compose.recipient.edit", { name: c.label }),
      removeLabel: c.valid ? t("primitive.chip.remove", { label: c.label }) : t("mail.compose.recipient.removeInvalid", { name: c.label }),
      onRemove: () => remove(i),
    };
    if (!c.valid) item.tone = "danger";
    return item;
  });
  return (
    <Popover
      open={open}
      onClose={() => {
        setDismissed(true);
        finish();
      }}
      side="bottom"
      align="start"
      fill
      width="anchor"
      autoFocus={false}
      anchor={
        <SearchChipField
          chips={items}
          value={draft}
          onChange={type}
          // The menu keeps the text until a row is picked or it's dismissed.
          onBlur={() => {
            if (!open) finish();
          }}
          onKey={key}
          variant="plain"
          keyboard="email"
          placeholder={placeholder ?? ""}
          accessibilityLabel={accessibilityLabel}
          {...(autoFocus === undefined ? {} : { autoFocus })}
          focusSeq={(focusSeq ?? 0) + refocus}
          testID={testID}
        />
      }
    >
      <Stack gap="0.5" padding="1" accessibilityRole="listbox" accessibilityLabel={accessibilityLabel} testID={`${testID}-suggest`}>
        {matches.map((c, i) => (
          <Pressable key={c.id} direction="horizontal" radius="sm" highlighted={i === active} onHover={() => setActive(i)} onPress={() => pick(c)} testID={`${testID}-suggest-${i}`}>
            <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
              <Avatar name={c.name ?? c.email} {...(url(c.email) === undefined ? {} : { src: url(c.email)! })} size="avatar.sm" />
              <Stack grow align="start">
                <Text lines={1}>{c.name ?? c.email}</Text>
                {c.name === undefined ? null : (
                  <Text variant="caption" color="text.secondary" lines={1}>
                    {c.email}
                  </Text>
                )}
              </Stack>
            </Stack>
          </Pressable>
        ))}
      </Stack>
    </Popover>
  );
}
